"use client";

import dayjs from "dayjs";
import { useTranslations } from "next-intl";
import { HeroIcon } from "@/components/hero-icon";
import { Skeleton } from "@/components/ui/skeleton";
import { useCieDetails } from "@/lib/queries/payment";
import { useCheckoutStore } from "@/lib/store/checkout";
import { ErrorBanner } from "./error-banner";
import { InstructionCard } from "./instruction-card";
import { InstructionContainer } from "./instruction-container";

export const CieInstructions = () => {
  const t = useTranslations("instructions");
  const today = dayjs().format("DD");
  const { data, isPending, isError, refetch } = useCieDetails();
  const business = useCheckoutStore((s) => s.business);

  return (
    <div className="space-y-8">
      {isError && <ErrorBanner refetch={refetch} />}
      <InstructionContainer isError={isError}>
        <p>{t("cie.intro")}</p>
        <div className="space-y-4">
          <p>{t("cie.step1")}</p>
          <p>{t("cie.step2")}</p>
          <p>{t("cie.step3")}</p>
          <p>{t("cie.step4")}</p>
          <InstructionCard
            title={t("cie.agreementNumber")}
            value="2490803"
            copyable
          />
          <WarningBanner
            title={t("cie.important")}
            message={t("cie.agreementNote", { merchant: business || "" })}
          />
          <p>{t("cie.step5")}</p>
          <div className="flex w-full flex-row items-center justify-center gap-2 self-center text-sm">
            {!isPending ? (
              <div className="self-center rounded-lg border border-border bg-background px-4 py-2 font-bold">
                {data?.reference.slice(0, 5)}
              </div>
            ) : (
              <Skeleton className="h-[38px] w-[88px]" />
            )}
            <span className="text-muted/40">-</span>
            {!isPending ? (
              <div className="self-center rounded-lg border border-border bg-background px-4 py-2 font-bold">
                {data?.reference.slice(5, 9)}
              </div>
            ) : (
              <Skeleton className="h-[38px] w-[88px]" />
            )}
            <span className="text-muted/40">-</span>
            {!isPending ? (
              <div className="self-center rounded-lg border border-border bg-background px-4 py-2 font-bold">
                {data?.reference.slice(9, 15)}
              </div>
            ) : (
              <Skeleton className="h-[38px] w-[88px]" />
            )}
            <span className="text-muted/40">-</span>
            {!isPending ? (
              <div className="self-center rounded-lg border border-border bg-background px-4 py-2 font-bold">
                {data?.reference.slice(15, 20)}
              </div>
            ) : (
              <Skeleton className="h-[38px] w-[88px]" />
            )}
          </div>
          <p>{t("cie.step6")}</p>
          <WarningBanner
            title={t("cie.important")}
            message={t("cie.amountWarning")}
          />
          <InstructionCard
            title={t("cie.amountToPay")}
            value={data?.amount.toLocaleString("es-MX", {
              style: "currency",
              currency: "MXN",
            })}
            skeletonClassName="h-5 w-40 rounded-lg"
            loading={isPending}
            copyable
          />
          <p>{t("cie.step7")}</p>
          <WarningBanner
            title={t("cie.important")}
            message={t("cie.conceptWarning")}
          />
          <InstructionCard
            title={t("cie.concept")}
            value={`Pago Practicaja ${today}`} // Do not translate (20 chars max)
            copyable
          />
          <p>{t("cie.step8")}</p>
          <p>{t("cie.step9")}</p>
        </div>
        <p className="text-muted text-sm">
          {t.rich("help", {
            email: (chunks) => (
              <a className="text-accent" href="mailto:info@paycode.com.mx">
                {chunks}
              </a>
            ),
            phone: (chunks) => (
              <a className="text-accent" href="tel:+525624870068">
                {chunks}
              </a>
            ),
          })}
        </p>
      </InstructionContainer>
    </div>
  );
};

type WarningBannerProps = {
  title: string;
  message: string;
};

const WarningBanner = ({ title, message }: WarningBannerProps) => (
  <div className="flex flex-row items-center rounded-xl border border-yellow-400/20 bg-yellow-400/20 p-4">
    <HeroIcon
      name="exclamation-triangle"
      className="mr-2 h-7 w-7 shrink-0 self-start text-yellow-500"
    />
    <div>
      <p className="font-bold text-xs text-yellow-950">{title}</p>
      <p className="text-xs text-yellow-950">{message}</p>
    </div>
  </div>
);
